import { compact, get, last } from 'lodash';
import { resolveWithConcurrency } from '../../helpers';
import { type WithRef } from '../interfaces';
import {
  limit,
  orderBy,
  query,
  startAfter,
  type CollectionReference,
  type Query,
} from './adaptor';
import { Firestore } from './firestore';

export interface IBatchedQueryOptions {
  /**
   * Number of documents fetched per Firestore round trip. Only one page of
   * source documents is held in memory at a time.
   */
  pageSize: number;
  /**
   * Field used to order and cursor through the collection. Defaults to `ref`,
   * which every document persists and which needs no composite index when the
   * query has no other constraints.
   */
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface IBatchedProcessOptions<T extends object>
  extends IBatchedQueryOptions {
  /**
   * How many `operatorFn` calls run in parallel within a single page.
   */
  concurrency: number;
  /**
   * Called after each page completes with the running total of documents
   * processed so far.
   */
  onProgress?: (processed: number) => void;
  /**
   * Called when `operatorFn` throws for a document. When provided the error is
   * swallowed and the document is skipped; when omitted the error propagates
   * and processing stops.
   */
  onError?: (error: unknown, item: WithRef<T>) => void;
}

/**
 * Cursor-based iteration over large Firestore collections with a bounded
 * memory footprint.
 *
 * Loading a whole collection with `Firestore.getDocs` and fanning out with
 * `resolveWithConcurrency` keeps every source document (and every result) on
 * the heap for the lifetime of the job, which is what caused the managed
 * report OOMs at 4GiB. `BatchedQuery` fetches one page at a time, processes
 * it with `resolveWithConcurrency`, and lets the page be garbage collected
 * before fetching the next. Only the caller's (typically much smaller)
 * projected results are retained.
 */
export class BatchedQuery {
  /**
   * Yields the query's documents one page at a time. Each page is fetched
   * lazily when the consumer asks for it, so the previous page can be released
   * before the next request is made.
   */
  static async *pages<T extends object>(
    source: CollectionReference<T> | Query<T>,
    options: IBatchedQueryOptions
  ): AsyncGenerator<WithRef<T>[]> {
    const sortField = options.sortField ?? 'ref';
    const sortOrder = options.sortOrder ?? 'asc';
    let cursor: WithRef<T> | undefined;

    do {
      const pageQuery = query(
        source,
        orderBy(sortField, sortOrder),
        limit(options.pageSize)
      );
      const cursoredQuery = cursor
        ? query(pageQuery, startAfter(get(cursor, sortField)))
        : pageQuery;

      const page = await Firestore.getDocs(cursoredQuery);
      if (!page.length) {
        return;
      }

      yield page;

      cursor = page.length >= options.pageSize ? last(page) : undefined;
    } while (cursor);
  }

  /**
   * Runs `operatorFn` over every document in the query, page by page, and
   * returns the compacted results. `undefined` results are dropped so callers
   * can filter inline without growing the result set.
   */
  static async process<T extends object, R>(
    source: CollectionReference<T> | Query<T>,
    options: IBatchedProcessOptions<T>,
    operatorFn: (item: WithRef<T>) => Promise<R | undefined>
  ): Promise<R[]> {
    const results: R[] = [];
    let processed = 0;

    for await (const page of BatchedQuery.pages(source, options)) {
      const pageResults = await resolveWithConcurrency(
        page,
        options.concurrency,
        (item) => BatchedQuery._runOperator(item, operatorFn, options.onError)
      );
      results.push(...compact(pageResults));

      processed += page.length;
      options.onProgress?.(processed);
    }

    return results;
  }

  private static async _runOperator<T extends object, R>(
    item: WithRef<T>,
    operatorFn: (item: WithRef<T>) => Promise<R | undefined>,
    onError?: (error: unknown, item: WithRef<T>) => void
  ): Promise<R | undefined> {
    if (!onError) {
      return operatorFn(item);
    }
    try {
      return await operatorFn(item);
    } catch (error) {
      onError(error, item);
      return undefined;
    }
  }
}
